/**
 * Variation Code Builder
 * variation codeの構築ロジックを統一管理
 */

(function() {
  'use strict';

  /**
   * バリアントからオプション値を取得（動的）
   * @param {Object} variant - バリアントオブジェクト（option1, option2, ... または options プロパティ）
   * @param {Array} shopifyOptions - Shopifyオプション定義 [{ name: '印刷色数', values: [...] }, ...]
   * @returns {Object} - オプション名をキーとしたオプション値のオブジェクト
   */
  function buildVariantOptions(variant, shopifyOptions) {
    const options = {};
    
    // variant.options が存在する場合（PrintHub形式）
    if (variant.options && typeof variant.options === 'object') {
      return { ...variant.options };
    }
    
    // variant.option1, option2, ... から取得（Liquid形式）
    if (Array.isArray(shopifyOptions) && shopifyOptions.length > 0) {
      shopifyOptions.forEach((opt, idx) => {
        const optionKey = 'option' + (idx + 1);
        const optionValue = variant[optionKey];
        if (optionValue && optionValue !== 'null' && optionValue !== '') {
          options[opt.name] = optionValue;
        }
      });
    }
    
    return options;
  }

  /**
   * variation codeを構築
   * @param {Object} variantOptions - バリアントのオプション値 { '用紙': 'コート90kg', '印刷色数': '片面１色', ... }
   * @param {Array} shopifyOptions - Shopifyオプション定義
   * @param {boolean} normalize - 正規化するかどうか（デフォルト: false - マッチング時のみtrue）
   * @returns {string} - variation code（例: "コート90kg_片面１色" または "ページ数_用紙_印刷色数"）
   * 
   * 【重要】price_matrixのキー形式に合わせる:
   * - ページ数なし: 用紙_印刷色数
   * - ページ数あり: ページ数_用紙_印刷色数
   */
  function buildVariationCode(variantOptions, shopifyOptions, normalize = false) {
    if (!variantOptions || typeof variantOptions !== 'object') {
      return window.VARIATION_CODE_STANDARD;
    }

    // 正規化関数（利用可能な場合）
    const normalizeStr = (normalize && window.normalizeString) ? window.normalizeString : (s) => s;

    const parts = [];

    // ページ数オプションを取得（動的）
    // フォールバック: window.VariationCodeConstants が未定義の場合
    const OPTION_NAMES = (window.VariationCodeConstants && window.VariationCodeConstants.OPTION_NAMES) || {
      PAPER: '用紙',
      PRINT_COLOR: '印刷色数',
      PAGE_COUNT: 'ページ数',
      PAGE_RANGE: 'ページ範囲',
      BODY_PAPER: '本文紙質',
      COVER_PAPER: '表紙紙質',
      TYPE: 'タイプ'
    };
    const pageCountOpt = Array.isArray(shopifyOptions) ? shopifyOptions.find(opt =>
      opt.name === OPTION_NAMES.PAGE_COUNT || opt.name === OPTION_NAMES.PAGE_RANGE
    ) : null;
    let pageCount = pageCountOpt ? variantOptions[pageCountOpt.name] : null;
    
    // 用紙オプションを取得（動的）
    const paperOpt = Array.isArray(shopifyOptions) ? shopifyOptions.find(opt => opt.name === OPTION_NAMES.PAPER) : null;
    let paper = paperOpt ? variantOptions[paperOpt.name] : null;

    // 印刷色数オプションを取得（動的）
    const printColorOpt = Array.isArray(shopifyOptions) ? shopifyOptions.find(opt => opt.name === OPTION_NAMES.PRINT_COLOR) : null;
    let printColor = printColorOpt ? variantOptions[printColorOpt.name] : null;
    
    // variation codeを構築
    // 【重要】price_matrixのキー形式: 用紙_印刷色数
    // ページ数あり: ページ数_用紙_印刷色数
    // ページ数なし: 用紙_印刷色数
    if (pageCount && pageCount !== 'null' && pageCount !== '') {
      parts.push(normalizeStr(pageCount));
    }
    // 用紙を先に追加（price_matrixのキー形式に合わせる）
    if (paper && paper !== 'null' && paper !== '' && paper !== pageCount) {
      parts.push(normalizeStr(paper));
    }
    // 印刷色数を後に追加
    if (printColor && printColor !== 'null' && printColor !== '') {
      parts.push(normalizeStr(printColor));
    }
    
    return parts.length > 0 ? parts.join(window.VARIATION_CODE_SEPARATOR) : window.VARIATION_CODE_STANDARD;
  }

  /**
   * variation codeを分解
   * @param {string} variationCode - variation code
   * @param {Array} shopifyOptions - Shopifyオプション定義
   * @returns {Object} - 分解されたオプション値 { paper, printColor, pageCount }
   */
  function parseVariationCode(variationCode, shopifyOptions) {
    if (!variationCode || variationCode === window.VARIATION_CODE_STANDARD) {
      return { paper: null, printColor: null, pageCount: null };
    }

    const parts = variationCode.split(window.VARIATION_CODE_SEPARATOR);
    // フォールバック: window.VariationCodeConstants が未定義の場合
    const OPTION_NAMES = (window.VariationCodeConstants && window.VariationCodeConstants.OPTION_NAMES) || {
      PAPER: '用紙',
      PRINT_COLOR: '印刷色数',
      PAGE_COUNT: 'ページ数',
      PAGE_RANGE: 'ページ範囲'
    };
    const hasPageCount = shopifyOptions.some(opt =>
      opt.name === OPTION_NAMES.PAGE_COUNT || opt.name === OPTION_NAMES.PAGE_RANGE
    );

    if (hasPageCount && parts.length >= 3) {
      // ページ数あり: ページ数_印刷色数_用紙
      return {
        pageCount: parts[0],
        printColor: parts[1],
        paper: parts[2]
      };
    } else if (parts.length >= 2) {
      // ページ数なし: 用紙_印刷色数
      return {
        paper: parts[0],
        printColor: parts.slice(1).join(window.VARIATION_CODE_SEPARATOR),
        pageCount: null
      };
    }

    return { paper: null, printColor: null, pageCount: null };
  }

  // グローバルに公開
  window.buildVariantOptions = buildVariantOptions;
  window.buildVariationCode = buildVariationCode;
  window.parseVariationCode = parseVariationCode;

})();

